import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from "recharts";
import {
  Box, Card, CardContent, Typography, Grid, CircularProgress, Paper,
  Chip, Table, TableBody, TableCell, TableHead, TableRow
} from "@mui/material";

// Icons
import SpeedIcon from "@mui/icons-material/Speed";
import WarningIcon from "@mui/icons-material/Warning";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import TimelineIcon from "@mui/icons-material/Timeline";

// [CONFIG] Change this to your server IP
const API_URL = "https://api.harishacrypto.xyz/service21/api/analytics";

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = async () => {
    try {
      const resp = await axios.get(API_URL);
      setData(resp.data);
      setLoading(false);
    } catch (e) {
      console.error(e);
      // Optional: Silent fail or show toast
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Real-time: Refresh every 5 seconds
    const interval = setInterval(fetchAnalytics, 5000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Helper for Cards
  const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
      <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography color="textSecondary" variant="subtitle2">{title}</Typography>
          <Typography variant="h4" fontWeight="bold">{value}</Typography>
        </Box>
        <Box sx={{ bgcolor: `${color}20`, borderRadius: "50%", p: 1.5, color: color }}>
          {icon}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box p={4}>
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          🚀 DriveSync Command Center
        </Typography>
      </motion.div>

      {/* 1. STATS GRID */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Active Drivers" value={data.stats.totalDrivers} icon={<DirectionsCarIcon />} color="#4f46e5" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Hazards" value={data.stats.totalHazards} icon={<WarningIcon />} color="#ef4444" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Avg Fleet Speed" value={`${data.stats.avgSpeed} km/h`} icon={<SpeedIcon />} color="#10b981" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Active Routes" value={data.stats.activeRoutes} icon={<TimelineIcon />} color="#f59e0b" />
        </Grid>
      </Grid>

      {/* 2. CHARTS */}
      <Grid container spacing={4} mb={4}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Hazard Frequency (Last 24h)</Typography>
            <Box height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="hazards" stroke="#ef4444" strokeWidth={3} activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Report Types</Typography>
            <Box height={300}>
               {/* Simple Bar Chart for Hazard Counts */}
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="hazards" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* 3. RECENT LOGS */}
      <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>Live Incident Logs</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><b>Type</b></TableCell>
                <TableCell><b>Location</b></TableCell>
                <TableCell><b>Time</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Chip 
                      label={log.type} 
                      color={log.type === "HARD_BRAKING" ? "warning" : log.type === "POTHOLE" ? "info" : "default"} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>{log.lat.toFixed(4)}, {log.lon.toFixed(4)}</TableCell>
                  <TableCell>{log.time}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
}